import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Fade,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Controller, useForm } from "react-hook-form";
import { parsePhoneNumber } from "react-phone-number-input";
import { useMutation } from "@tanstack/react-query";
import { joinMailingList } from "../../../utility/httpActions/mailingListActions";
import { isValidEmail } from "../../../utility/utilityFunctions";
import { getSupportedVisitorCountry } from "../../../utility/geo";
import { landingColors } from "../landingColors";
import CountryPhoneInput, {
  validatePhoneForCountry,
} from "../../../common-components/PhoneNumberInput/CountryPhoneInput";

const INTEREST_OPTIONS = [
  { value: "BUY", label: "Buying" },
  { value: "SELL", label: "Selling" },
  { value: "BOTH", label: "Both" },
];

// Shown under the app download banner. Deliberately short, since every extra
// field costs signups. Email is required (it's an email list), phone is
// optional, and one optional chip lets us segment buyers from sellers in Brevo.
const MailingListForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submittedName, setSubmittedName] = useState("");

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      name: "",
      email: "",
      // Pre-select the visitor's IP country when we support it, else Nigeria.
      country: getSupportedVisitorCountry("NG"),
      phoneNumber: "",
      interest: "",
    },
  });

  const mutation = useMutation({
    mutationFn: joinMailingList,
    onSuccess: () => setSubmitted(true),
  });

  const onSubmit = ({ phoneNumber, country, ...values }) => {
    setSubmittedName(values.name);
    // Send the number in E.164 plus its country (a typed "+44..." overrides the
    // picker). Both are omitted when no phone was entered.
    const parsed = phoneNumber ? parsePhoneNumber(phoneNumber, country) : null;
    mutation.mutate({
      ...values,
      ...(parsed && {
        phoneNumber: parsed.number,
        country: parsed.country || country,
      }),
    });
  };

  const firstName = submittedName.trim().split(/\s+/)[0];

  return (
    // No card styling of its own: GetTheAppSection puts it in the same card as
    // the app download banner.
    <Box sx={{ overflow: "hidden" }}>
      <Fade
        in
        appear
        key={submitted ? "success" : "form"}
        timeout={1000}
        easing="ease-in-out"
      >
        <Box>
          {submitted ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ color: landingColors.textPrimary, mb: 1 }}
              >
                You're in, {firstName}!
              </Typography>
              <Typography sx={{ color: landingColors.textSecondary }}>
                Keep an eye on your inbox. We'll be in touch soon.
              </Typography>
            </Box>
          ) : (
            <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: landingColors.textPrimary, mb: 1 }}
                >
                  Stay in the loop
                </Typography>
                <Typography sx={{ color: landingColors.textSecondary }}>
                  Drop your name and email and we'll start an email chat with
                  you, with tips, updates and support to help you get the most
                  out of Ngwaahia.
                </Typography>
              </Box>

              {mutation.isError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {mutation.error?.data?.message ||
                    "Something went wrong. Please try again."}
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                    Your name
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    inputProps={{ "aria-label": "Your name" }}
                    {...register("name", { required: "Name is required" })}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    sx={{ bgcolor: landingColors.background, borderRadius: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                    Email
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="email"
                    inputProps={{ "aria-label": "Email" }}
                    {...register("email", {
                      required: "Email is required",
                      validate: (value) =>
                        isValidEmail(value) ||
                        "Please enter a valid email address",
                    })}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    sx={{ bgcolor: landingColors.background, borderRadius: 1 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                    Phone number (Optional)
                  </Typography>
                  <CountryPhoneInput
                    control={control}
                    size="small"
                    bgcolor={landingColors.background}
                    focusColor={landingColors.primary}
                    rules={{
                      validate: (value, formValues) =>
                        !value || validatePhoneForCountry(value, formValues),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                    What brings you here? (Optional)
                  </Typography>
                  <Controller
                    control={control}
                    name="interest"
                    render={({ field }) => (
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {INTEREST_OPTIONS.map(({ value, label }) => {
                          const selected = field.value === value;
                          return (
                            <Chip
                              key={value}
                              label={label}
                              clickable
                              aria-pressed={selected}
                              // Tapping the selected chip clears it again.
                              onClick={() =>
                                field.onChange(selected ? "" : value)
                              }
                              sx={{
                                fontWeight: 600,
                                bgcolor: selected
                                  ? landingColors.primary
                                  : landingColors.background,
                                color: selected
                                  ? landingColors.surface
                                  : landingColors.textPrimary,
                                "&:hover": {
                                  bgcolor: selected
                                    ? landingColors.accent
                                    : landingColors.background,
                                },
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  />
                </Grid>
              </Grid>

              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Button
                  type="submit"
                  disabled={mutation.isPending}
                  variant="contained"
                  endIcon={
                    // Keyed so the arrow and spinner fade into each other.
                    <Fade in appear key={mutation.isPending} timeout={200}>
                      {mutation.isPending ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <ArrowForwardIcon />
                      )}
                    </Fade>
                  }
                  sx={{
                    bgcolor: landingColors.primary,
                    "&:hover": { bgcolor: landingColors.accent },
                    borderRadius: 6,
                    fontWeight: 700,
                    px: 3,
                    py: 1.25,
                  }}
                >
                  {mutation.isPending ? "Joining..." : "Join the mailing list"}
                </Button>
              </Box>

              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  textAlign: "center",
                  mt: 2,
                  color: landingColors.disabled,
                }}
              >
                🔒 We'll only email you about Ngwaahia. Unsubscribe anytime.
              </Typography>
            </Box>
          )}
        </Box>
      </Fade>
    </Box>
  );
};

export default MailingListForm;
